/* graphUtils.ts
   Utilities for converting policy rules into ReactFlow elements and
   applying automatic layout using dagre.
   Extracted from GraphView.tsx to decouple conversion and layout logic.
*/

import dagre from 'dagre';
import type { Node, Edge } from 'reactflow';
import { MarkerType } from 'reactflow';
import type {
  PolicyRuleIf,
  Condition,
  NestedCondition,
  NotCondition,
  CountCondition,
  SimpleCondition,
} from '../../../../types/policy';
import {
  isSimpleCondition,
  isCountCondition,
  isNestedCondition,
  isNotCondition,
} from '../types/policy';

// Node size constants for layout
export const nodeWidth = 180;
export const nodeHeight = 120;

/**
 * Type-safe helper to get operator value from condition
 */
function getOperatorValue(condition: CountCondition | SimpleCondition, operator: string): unknown {
  if (operator in condition) {
    return (condition as Record<string, unknown>)[operator];
  }
  return undefined;
}

/**
 * Type-safe helper to get children from nested condition
 */
function getConditionChildren(condition: NestedCondition): Condition[] {
  if (condition.allOf) {
    return condition.allOf;
  }
  if (condition.anyOf) {
    return condition.anyOf;
  }
  return [];
}

/**
 * Convert a policy 'if' clause into ReactFlow nodes and edges.
 */
export function policyToFlowElements(
  policyRuleIf: PolicyRuleIf | undefined
): { nodes: Node[]; edges: Edge[] } {
  const nodes: Node[] = [];
  const edges: Edge[] = [];
  if (!policyRuleIf) return { nodes, edges };
  let nodeIdCounter = 0;
  let edgeIdCounter = 0;

  function processNode(
    conditionOrRule: PolicyRuleIf | Condition | NestedCondition | NotCondition,
    parentId: string | null,
    path: (string | number)[]
  ): string | null {
    const currentNodeId = `node-${nodeIdCounter++}`;
    let nodeType: string | null = null;
    let nodeData: any = { path };
    let children: Condition[] = [];
    let childPrefix: (string | number)[] = [];

    if (isNotCondition(conditionOrRule)) {
      nodeType = 'group';
      nodeData = { ...nodeData, label: 'NOT', isGroup: true, isNot: true };
      const inner = conditionOrRule.not;
      if (
        inner &&
        (isSimpleCondition(inner) || isCountCondition(inner) || isNestedCondition(inner) || isNotCondition(inner))
      ) {
        children = [inner];
      }
      childPrefix = ['not'];
    } else if (isNestedCondition(conditionOrRule)) {
      nodeType = 'group';
      if (conditionOrRule.allOf) {
        nodeData = { ...nodeData, label: 'allOf', isGroup: true };
        children = conditionOrRule.allOf;
        childPrefix = ['allOf'];
      } else {
        nodeData = { ...nodeData, label: 'anyOf', isGroup: true };
        children = conditionOrRule.anyOf || [];
        childPrefix = ['anyOf'];
      }
    } else if (isCountCondition(conditionOrRule)) {
      nodeType = 'count';
      const cc: CountCondition = conditionOrRule;
      const op = Object.keys(cc).find((k) => k !== 'count') || 'equals';
      const val = getOperatorValue(cc, op);
      nodeData = {
        ...nodeData,
        label: `Count(${cc.count.field}) ${op} ${val}`,
        countData: cc,
        isCount: true,
      };
      if (cc.count.where && Object.keys(cc.count.where).length > 0) {
        processNode(cc.count.where, currentNodeId, [...path, 'count', 'where']);
      }
    } else if (isSimpleCondition(conditionOrRule)) {
      nodeType = 'condition';
      const sc = conditionOrRule;
      const op = Object.keys(sc).find((k) => k !== 'field') || 'equals';
      const val = getOperatorValue(sc, op);
      const display = Array.isArray(val) ? `[${val.join(', ')}]` : JSON.stringify(val);
      nodeData = { ...nodeData, label: `${sc.field} ${op} ${display}`, conditionData: sc, isCondition: true, isNot: false };
    } else {
      // Unknown structure
      return null;
    }

    // Push node with layout style
    nodes.push({
      id: currentNodeId,
      type: nodeType,
      data: nodeData,
      position: { x: 0, y: 0 },
      style: { width: nodeWidth, height: nodeHeight, backgroundColor: 'transparent', border: 'none', padding: 0 },
    });
    if (parentId) {
      edges.push({ id: `edge-${edgeIdCounter++}`, source: parentId, target: currentNodeId, type: 'smoothstep', markerEnd: { type: MarkerType.ArrowClosed } });
    }
    // Recurse children
    children.forEach((child, idx) => {
      const suffix = nodeData.isNot ? [] : [idx];
      processNode(child, currentNodeId, [...path, ...childPrefix, ...suffix]);
    });
    return currentNodeId;
  }

  processNode(policyRuleIf, null, []);
  return { nodes, edges };
}

/**
 * Applies a dagre layout to the nodes and edges, returning updated positions.
 */
export function getLayoutedElements(
  nodes: Node[],
  edges: Edge[],
  direction: 'TB' | 'LR' = 'TB'
): { nodes: Node[]; edges: Edge[] } {
  const graph = new dagre.graphlib.Graph();
  graph.setDefaultEdgeLabel(() => ({}));
  graph.setGraph({ rankdir: direction, nodesep: 100, ranksep: 100 });

  nodes.forEach((n) => graph.setNode(n.id, { width: nodeWidth, height: nodeHeight }));
  edges.forEach((e) => graph.setEdge(e.source, e.target));

  dagre.layout(graph);

  const layoutedNodes = nodes.map((n) => {
    const nodePosition = graph.node(n.id) as { x: number; y: number };
    n.position = { x: nodePosition.x - nodeWidth / 2, y: nodePosition.y - nodeHeight / 2 };
    return n;
  });

  return { nodes: layoutedNodes, edges };
} 